import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Trash2, Shield, Edit2, Lock, Building2, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import CompanySettingsCard from '../components/CompanySettingsCard';

const ALL_PAGES = [
  { path: '/', label: 'Dashboard' },
  { path: '/customers', label: 'Kunder' },
  { path: '/cleaning', label: 'Rens planlegging' },
  { path: '/montering', label: 'Montering' },
  { path: '/loaned-equipment', label: 'Utlånt utstyr' },
  { path: '/reports', label: 'Rapporter' },
  { path: '/admin', label: 'Brukere' },
];

export const AdminPanel = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [accessDialogUser, setAccessDialogUser] = useState(null);
  const [accessPages, setAccessPages] = useState([]);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'tekniker',
    customer_id: '',
  });
  const [editUser, setEditUser] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/my-companies`);
      setAllCompanies(res.data);
    } catch (err) { console.warn('[AdminPanel]', err); }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return '';
    const c = allCompanies.find(co => co.id === companyId);
    return c ? c.name : '';
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`);
      setCustomers(res.data.filter(c => c.is_active !== false));
    } catch (err) { console.warn('[AdminPanel]', err); }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Kunne ikke hente brukere');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, { ...newUser, company_id: currentUser?.company_id });
      toast.success('Bruker opprettet!');
      setDialogOpen(false);
      setNewUser({ email: '', password: '', name: '', role: 'tekniker', customer_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke opprette bruker');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    
    try {
      const updateData = {
        name: editUser.name,
        email: editUser.email,
        role: editUser.role
      };
      
      // Kun inkluder passord hvis det er fylt ut
      if (editUser.newPassword && editUser.newPassword.trim()) {
        updateData.password = editUser.newPassword;
      }
      
      await axios.put(`${API}/users/${editUser.id}`, updateData);
      toast.success('Bruker oppdatert!');
      setEditDialogOpen(false);
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere bruker');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, { is_active: user.is_active === false });
      toast.success(user.is_active === false ? 'Bruker aktivert' : 'Bruker deaktivert (historikk beholdes)');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere bruker');
    }
  };

  const openEditDialog = (user) => {
    setEditUser({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      newPassword: ''
    });
    setEditDialogOpen(true);
  };

  const openAccessDialog = (user) => {
    setAccessDialogUser(user);
    setAccessPages(user.page_access && user.page_access.length > 0 ? [...user.page_access] : []);
  };

  const [companyDialogUser, setCompanyDialogUser] = useState(null);
  const [userCompanyIds, setUserCompanyIds] = useState([]);

  const openCompanyDialog = (user) => {
    setCompanyDialogUser(user);
    setUserCompanyIds(user.company_ids && user.company_ids.length > 0 ? [...user.company_ids] : user.company_id ? [user.company_id] : []);
  };

  const toggleCompanyId = (id) => {
    setUserCompanyIds(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const handleSaveCompanies = async () => {
    try {
      await axios.put(`${API}/users/${companyDialogUser.id}`, { company_ids: userCompanyIds });
      toast.success('Selskapstilknytning oppdatert');
      setCompanyDialogUser(null);
      fetchUsers();
    } catch { toast.error('Kunne ikke oppdatere'); }
  };

  const togglePageAccess = (path) => {
    setAccessPages(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };

  const handleSaveAccess = async () => {
    try {
      await axios.put(`${API}/users/${accessDialogUser.id}`, {
        page_access: accessPages.length > 0 ? accessPages : null,
      });
      toast.success('Tilganger oppdatert');
      setAccessDialogUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Kunne ikke oppdatere tilganger');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    try {
      await axios.delete(`${API}/users/${deleteUserId}`);
      toast.success('Bruker slettet');
      setDeleteUserId(null);
      fetchUsers();
    } catch (error) {
      toast.error('Kunne ikke slette bruker');
      setDeleteUserId(null);
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'system_admin': return 'System Admin';
      case 'admin': return 'Administrator';
      case 'tekniker': return 'Tekniker';
      case 'lager': return 'Lager';
      case 'kunde': return 'Kunde';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Laster...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Brukeradministrasjon
          </h1>
          <p className="text-slate-600">Administrer brukere og tilganger</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="btn-primary bg-slate-900 hover:bg-slate-800">
              <Plus className="mr-2 h-4 w-4" />
              Ny Bruker
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Legg til ny bruker</DialogTitle>
              <DialogDescription>Opprett en ny bruker med tilgang til systemet</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="user_name">Navn *</Label>
                <Input
                  id="user_name"
                  data-testid="user-name-input"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                  className="input-field"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="user_email">E-post *</Label>
                <Input
                  id="user_email"
                  data-testid="user-email-input"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                  className="input-field"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="user_password">Passord *</Label>
                <Input
                  id="user_password"
                  data-testid="user-password-input"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  className="input-field"
                  placeholder="Minimum 6 tegn"
                />
              </div>
              <div className="space-y-2">
                <Label>Rolle *</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tekniker">Tekniker</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="system_admin">System Admin</SelectItem>
                    <SelectItem value="lager">Lager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" data-testid="submit-user-button" className="w-full btn-primary bg-slate-900 hover:bg-slate-800">
                Opprett Bruker
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {(currentUser?.role === 'admin' || currentUser?.role === 'system_admin') && <CompanySettingsCard />}

      <Card>
        <CardHeader>
          <CardTitle>Brukere</CardTitle>
          <CardDescription>Alle brukere med tilgang til systemet</CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-slate-600 text-center py-8">Ingen brukere funnet</p>
          ) : (
            <div className="space-y-3">
              {users.filter(u => u.role !== 'kunde').map((user) => (
                <div
                  key={user.id}
                  data-testid={`user-card-${user.id}`}
                  className="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      user.role === 'system_admin' 
                        ? 'bg-purple-100' 
                        : user.role === 'admin' 
                          ? 'bg-brand-100' 
                          : 'bg-slate-200'
                    }`}>
                      <Shield className={`h-5 w-5 ${
                        user.role === 'system_admin' 
                          ? 'text-purple-600' 
                          : user.role === 'admin' 
                            ? 'text-brand-600' 
                            : 'text-slate-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <p className="text-sm text-slate-600">{user.email}</p>
                      {user.company_id && <p className="text-xs text-slate-400">{getCompanyName(user.company_id)}</p>}
                      {user.company_ids && user.company_ids.length > 1 && (
                        <p className="text-xs text-brand-500">{user.company_ids.length} selskaper</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full ${
                        user.role === 'system_admin'
                          ? 'bg-purple-100 text-purple-800'
                          : user.role === 'admin'
                            ? 'bg-brand-100 text-brand-800'
                            : 'bg-slate-200 text-slate-800'
                      }`}
                    >
                      {getRoleName(user.role)}
                    </span>
                    {user.is_active === false && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700" data-testid={`inactive-badge-${user.id}`}>
                        Deaktivert
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(user)}
                      className="text-slate-600 hover:text-slate-700"
                      data-testid={`edit-user-${user.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openCompanyDialog(user)}
                      className="text-brand-600 hover:text-brand-700"
                      data-testid={`company-user-${user.id}`}
                      title="Selskapstilknytning"
                    >
                      <Building2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openAccessDialog(user)}
                      className="text-brand-600 hover:text-brand-700"
                      data-testid={`access-user-${user.id}`}
                      title="Sidetilganger"
                    >
                      <Lock className="h-4 w-4" />
                    </Button>
                    {user.role !== 'system_admin' && user.id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(user)}
                        className={user.is_active === false ? 'text-brand-600 hover:text-brand-700' : 'text-amber-600 hover:text-amber-700'}
                        data-testid={`toggle-active-${user.id}`}
                        title={user.is_active === false ? 'Aktiver bruker' : 'Deaktiver bruker'}
                      >
                        <Power className="h-4 w-4" />
                      </Button>
                    )}
                    {user.role !== 'system_admin' && user.id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteUserId(user.id)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>System Informasjon</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Totalt brukere:</span>
              <span className="font-medium text-slate-900">{users.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Administratorer:</span>
              <span className="font-medium text-slate-900">
                {users.filter(u => u.role === 'admin' || u.role === 'system_admin').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Teknikere:</span>
              <span className="font-medium text-slate-900">
                {users.filter(u => u.role === 'tekniker').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Lager:</span>
              <span className="font-medium text-slate-900">
                {users.filter(u => u.role === 'lager').length}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rediger bruker</DialogTitle>
            <DialogDescription>Oppdater brukerinformasjon og tilgang</DialogDescription>
          </DialogHeader>
          {editUser && (
            <form onSubmit={handleEditUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_user_name">Navn *</Label>
                <Input
                  id="edit_user_name"
                  value={editUser.name}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_user_email">E-post *</Label>
                <Input
                  id="edit_user_email"
                  type="email"
                  value={editUser.email}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Rolle *</Label>
                <Select
                  value={editUser.role}
                  onValueChange={(value) => setEditUser({ ...editUser, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tekniker">Tekniker</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="system_admin">System Admin</SelectItem>
                    <SelectItem value="lager">Lager</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  <strong>Tekniker:</strong> Kan registrere service og se kunder<br />
                  <strong>Administrator:</strong> Full tilgang til alle funksjoner<br />
                  <strong>System Admin:</strong> Full tilgang + selskapsadministrasjon<br />
                  <strong>Lager:</strong> Kun tilgang til utlånt utstyr
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_user_password">Nytt passord (valgfritt)</Label>
                <Input
                  id="edit_user_password"
                  type="password"
                  value={editUser.newPassword}
                  onChange={(e) => setEditUser({ ...editUser, newPassword: e.target.value })}
                  placeholder="La stå tomt for å beholde eksisterende"
                />
                <p className="text-xs text-slate-500">Fyll kun ut hvis du vil endre passordet</p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                  Avbryt
                </Button>
                <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">
                  Lagre Endringer
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={!!deleteUserId} onOpenChange={(open) => !open && setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette bruker?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil slette denne brukeren? Denne handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Page Access Dialog */}
      <Dialog open={!!accessDialogUser} onOpenChange={(open) => !open && setAccessDialogUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sidetilganger</DialogTitle>
            <DialogDescription>Velg hvilke sider {accessDialogUser?.name} skal ha tilgang til. Ingen avkryssing = tilgang til alt.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {ALL_PAGES.map(page => (
              <div key={page.path} className="flex items-center gap-3 py-1" data-testid={`access-page-${page.path.replace('/', '') || 'dashboard'}`}>
                <Checkbox
                  checked={accessPages.includes(page.path)}
                  onCheckedChange={() => togglePageAccess(page.path)}
                />
                <Label className="cursor-pointer" onClick={() => togglePageAccess(page.path)}>{page.label}</Label>
              </div>
            ))}
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setAccessDialogUser(null)} className="flex-1">Avbryt</Button>
            <Button onClick={handleSaveAccess} className="flex-1 bg-brand-600 hover:bg-brand-700" data-testid="save-access-button">Lagre tilganger</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Company Assignment Dialog */}
      <Dialog open={!!companyDialogUser} onOpenChange={(open) => !open && setCompanyDialogUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Selskapstilknytning</DialogTitle>
            <DialogDescription>Velg hvilke selskaper {companyDialogUser?.name} skal ha tilgang til.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {allCompanies.map(company => (
              <div key={company.id} className="flex items-center gap-3 py-1" data-testid={`company-assign-${company.id}`}>
                <Checkbox
                  checked={userCompanyIds.includes(company.id)}
                  onCheckedChange={() => toggleCompanyId(company.id)}
                />
                <span className="text-sm">{company.name}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setCompanyDialogUser(null)} className="flex-1">Avbryt</Button>
            <Button onClick={handleSaveCompanies} className="flex-1 bg-brand-600 hover:bg-brand-700" data-testid="save-companies-button">Lagre selskaper</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
